/* eslint-disable */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { submitToImpartner } from "@/lib/trials";
import "./CognitoFormComponent.css";
interface CognitoEntry {
  [key: string]: any;
}
interface CognitoResponse {
  data: {
    entry: CognitoEntry;
  };
}
declare const _Cognito: {
  prefill: (data: CognitoEntry) => void;
  on: (event: string, callback: (response: CognitoResponse) => void) => void;
};
interface CognitoFormComponentProps {
  formData: {
    formId: string;
  };
  prefillData: Record<string, any>;
  productData: {
    sku: string;
  };
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setError: React.Dispatch<React.SetStateAction<boolean>>;
  setErrorText: React.Dispatch<React.SetStateAction<string>>;
  startTrial: (setLoading: React.Dispatch<React.SetStateAction<boolean>>, setError: React.Dispatch<React.SetStateAction<boolean>>, setErrorText: React.Dispatch<React.SetStateAction<string>>, sku?: string, accessToken?: string | null) => void;
  /** TID access token for trial API (from login context). */
  accessToken?: string | null;
  submitToWorkato?: (values: CognitoEntry) => void;
  loading?: boolean;
  partnerPage?: boolean;
  trialIdFromAPI?: string;
}
export const CognitoFormComponent: React.FC<CognitoFormComponentProps> = ({
  formData,
  prefillData,
  setLoading,
  setError,
  setErrorText,
  productData,
  startTrial,
  accessToken,
  partnerPage,
  trialIdFromAPI
}) => {
  const [scriptAdded, setScriptAdded] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const formContainerRef = useRef<HTMLDivElement>(null);
  const {
    formId
  } = formData;
  const {
    sku
  } = productData;
  // Validation flags
  const hasValidPrefillData = !!prefillData;
  const hasValidFormId = !!formId;

  // Early validation with proper error handling
  if (!hasValidPrefillData) {
    setError(true);
    setErrorText("Missing prefill data");
  }
  if (!hasValidFormId) {
    setError(true);
    setErrorText("Missing form ID");
  }
  const handleScriptLoad = useCallback(() => {
    try {
      // Add extra check for Turbopack compatibility
      if (typeof window === "undefined" || !window.Cognito) {
        setError(true);
        setErrorText("Cognito Forms not available");
        setLoading(false);
        return;
      }

      // Turbopack-safe API initialization
      const api = window.Cognito("0cXQNplKOk-LZ2g6xRL5fQ");

      // Ensure container exists before mounting (Turbopack timing issue)
      const container = document.getElementById("trial-cognito");
      if (!container) {
        setTimeout(() => handleScriptLoad(), 100);
        return;
      }
      const form1 = api.mount(formId, "#trial-cognito");
      if (prefillData && typeof prefillData === "object") {
        try {
          form1.prefill(prefillData);
        } catch (_prefillError) {
          // Error prefilling form
          // Continue without prefill rather than failing completely
        }
      }
      setLoading(false);
      form1.on("afterSubmit", (e: any) => {
        try {
          const submittedData = e?.data;
          const storedPartnerId = sessionStorage.getItem("utm_impartnerId");
          if (partnerPage && storedPartnerId) {
            submitToImpartner(storedPartnerId, prefillData, submittedData?.entry, setError, productData.sku);
          } else if (partnerPage && !storedPartnerId) {
            setError(true);
            return;
          }
          setTimeout(() => {
            try {
              startTrial(setLoading, setError, setErrorText, trialIdFromAPI, accessToken ?? null);
            } catch (_trialError) {
              setError(true);
              setErrorText("Trial setup encountered an issue. Please contact support.");
              setLoading(false);
            }
          }, 0);
        } catch (_submitError) {
          setError(true);
          setErrorText("Form submission completed with warnings.");
          setLoading(false);
        }
      });
    } catch (_initError) {
      setError(true);
      setErrorText("Failed to initialize form");
      setLoading(false);
    }
  }, [formId, prefillData, startTrial, setLoading, setError, setErrorText, sku, partnerPage, productData.sku, accessToken, trialIdFromAPI]);
  useEffect(() => {
    // Only proceed if validation passes
    if (!hasValidPrefillData || !hasValidFormId) {
      return;
    }
    const container_0 = formContainerRef.current;
    if (!container_0) {
      return;
    }

    // Clear any existing content on refresh
    if (container_0.hasChildNodes()) {
      container_0.innerHTML = "";
    }
    if (scriptAdded) {
      if (!scriptLoaded) {
        handleScriptLoad();
        setScriptLoaded(true);
      }
    } else {
      // Check if Cognito is already available (page refresh case)
      if (window.Cognito) {
        setScriptAdded(true);
      } else {
        const script = document.createElement("script");
        script.defer = true;
        script.src = "https://www.cognitoforms.com/f/seamless.js";
        script.onload = () => {
          if (window?.Cognito) {
            setScriptAdded(true);
          } else {
            setError(true);
            setErrorText("Failed to load Cognito Forms");
            setLoading(false);
          }
        };
        script.onerror = () => {
          setError(true);
          setErrorText("Failed to load form script");
          setLoading(false);
        };
        container_0.appendChild(script);
      }
    }

    // const script = document.createElement("script")
    // script.src = "https://www.cognitoforms.com/f/seamless.js"
    // script.setAttribute("data-key", "0cXQNplKOk-LZ2g6xRL5fQ")
    // script.setAttribute("data-form", formId)

    // script.onload = () => {
    //   if (window.Cognito) {
    //     console.log("Cognito Forms script loaded with prefill data:", prefillData)
    //     // window.Cognito.resize()
    //     window.Cognito.prefill(prefillData)
    //     setLoading(false) // Form is now loaded and prefilled
    //     window.Cognito.on("afterSubmit", () => {
    //       startTrial(setLoading, setError, setErrorText, sku, Trial_Duration)
    //     })
    //   }
    // }

    // script.onerror = () => {
    //   console.error("Failed to load Cognito Forms script.")
    //   setError(true)
    //   setLoading(false)
    // }

    // formContainerRef.current.appendChild(script)

    return () => {
      if (container_0) {
        container_0.innerHTML = "";
      }
    };
  }, [hasValidPrefillData, hasValidFormId, formId, prefillData, startTrial, setLoading, setError, setErrorText, sku, scriptAdded, scriptLoaded, handleScriptLoad]);

  // Return error states if validation fails
  if (!hasValidPrefillData) {
    return <div className="trial-cognito error">Missing form data</div>;
  }
  if (!hasValidFormId) {
    return <div className="trial-cognito error">Missing form configuration</div>;
  }
  return <div className="trial-cognito" ref={formContainerRef} id="trial-cognito" />;
};